import { ref, onMounted, onBeforeUnmount, computed, watch, unref, type Ref } from 'vue';
import * as rive from '@rive-app/canvas';
import { useInstance } from './useInstance';

/**@Info Keeping it in assets stopped working at some point. if in assets there is an error from riveloader, while on vercel deployment. Hosting by rive costs 50 bucks per year, so this is a workaround for that. */
const RIVE_LOADER_LIGHT = 'https://static.lendi.pl/shared/loader-lightmode.riv';
const RIVE_LOADER_DARK = 'https://static.lendi.pl/shared/loader-darkmode.riv';

export function useRiveLoader(options: {
  width?: Ref<number> | number;
  height?: Ref<number> | number;
}) {
  const { $vuetify } = useInstance();
  const isDarkMode = computed<boolean>(() => $vuetify.theme.dark);

  const {
    width = 164,
    height = 164,
  } = options;

  const canvasWidth = computed(() => unref(width));
  const canvasHeight = computed(() => unref(height));

  const riveCanvas = ref<HTMLCanvasElement | null>(null);
  let riveInstance: rive.Rive | null = null;

  const syncCanvasSize = () => {
    const canvas = riveCanvas.value;
    if (!canvas) return;

    // Layout size must stay on the CSS box. resizeDrawingSurfaceToCanvas reads
    // getBoundingClientRect() and writes canvas.width/height as that size times
    // devicePixelRatio. Without a CSS size, that write becomes the layout size
    // and the loader overflows the mobile viewport.
    canvas.style.width = `${canvasWidth.value}px`;
    canvas.style.height = `${canvasHeight.value}px`;
    riveInstance?.resizeDrawingSurfaceToCanvas();
  };

  const initRive = () => {
    if (!riveCanvas.value) return;

    syncCanvasSize();

    riveInstance = new rive.Rive({
      src: isDarkMode.value ? RIVE_LOADER_DARK : RIVE_LOADER_LIGHT,
      canvas: riveCanvas.value,
      autoplay: true,
      onLoad: () => {
        syncCanvasSize();
      },
    });
  };

  onMounted(() => {
    initRive();
  });

  // Default flush is "pre", so this would run before Vue patches :width/:height.
  // Those attributes reset the bitmap and would clobber the DPR-sized buffer
  // written by resizeDrawingSurfaceToCanvas().
  watch([canvasWidth, canvasHeight], syncCanvasSize, { flush: 'post' });

  onBeforeUnmount(() => {
    if (riveInstance) {
      riveInstance.cleanup();
      riveInstance = null;
    }
  });

  return {
    riveCanvas,
    canvasWidth,
    canvasHeight,
    initRive,
  };
}
